import React, { useState } from 'react';
import { Tabs, Tab, Box, Typography, Button, Autocomplete, TextField } from '@mui/material';
import { Download, CheckCircle } from '@mui/icons-material';

const dqTypes = [
  'Row Count',
  'Row vs Row',
  'Null Check',
  'Duplicate Check',
  'KPI Check',
  'Custom Check'
];

const DQ = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedType, setSelectedType] = useState('');
  const [apiResult, setApiResult] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleSubmit = () => {
    setTimeout(() => {
      setApiResult({ report: `DQ Type Selected: ${selectedType}` });
      setTabIndex(1);
    }, 1000);
  };

  const handleDownloadPDF = () => {
    if (!apiResult || !apiResult.report) return;
    import('jspdf').then(jsPDFModule => {
      const doc = new jsPDFModule.jsPDF();
      doc.text(String(apiResult.report), 10, 10);
      doc.save('DQ_Output.pdf');
    });
  };

  return (
    <Box sx={{ width: '100%', marginTop: -8 }}>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{
          pl: 0,
          '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
          '& .MuiTab-root': {
            minWidth: 120,
            fontWeight: 'bold',
            textTransform: 'none',
            paddingRight: 0,
            pl: 0,
          },
        }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 1 Input */}
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={24}
                  height={24}
                  borderRadius="50%"
                  bgcolor={apiResult?.report ? "#4caf50" : (tabIndex === 0 ? "#1976d2" : "white")}
                  border="1.5px solid"
                  borderColor={apiResult?.report ? "#4caf50" : (tabIndex === 0 ? "#1976d2" : "black")}
                  mr={1}
                  transition="all 0.2s"
                >
                  {apiResult?.report ? (
                    <CheckCircle sx={{ color: "white", fontSize: 20 }} />
                  ) : (
                    <Typography variant="subtitle2" sx={{ color: tabIndex === 0 ? "white" : "black", fontWeight: 600 }}>1</Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontWeight: tabIndex === 0 ? 700 : 400,
                    color: tabIndex === 0 ? "primary.main" : "black",
                    userSelect: "none",
                    transition: "all 0.2s"
                  }}
                >
                  Input
                </Typography>
              </Box>
              {/* Arrow */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 44,
                  ml: 0.5,
                  mr: 0.1,
                }}
              >
                <Typography sx={{ color: "#bdbdbd", fontWeight: 700, fontSize: 44, lineHeight: 1 }}>
                  &rarr;
                </Typography>
              </Box>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingRight: 0, paddingLeft: 0 }}
        />
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 2 Result */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={24}
                height={24}
                borderRadius="50%"
                bgcolor={tabIndex === 1 ? "#1976d2" : "white"}
                border="1.5px solid"
                borderColor={tabIndex === 1 ? "#1976d2" : "black"}
                mr={1}
                transition="all 0.2s"
              >
                <Typography variant="subtitle2" sx={{ color: tabIndex === 1 ? "white" : "black", fontWeight: 600 }}>2</Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: tabIndex === 1 ? 700 : 400,
                  color: tabIndex === 1 ? "primary.main" : "black",
                  userSelect: "none",
                  transition: "all 0.2s"
                }}
              >
                Result
              </Typography>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingLeft: 0 }}
        />
      </Tabs>

      {/* Input Content */}
      {tabIndex === 0 && (
        <Box sx={{ marginTop: 2, maxWidth: 350 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
            Type
          </Typography>
          <Autocomplete
            options={dqTypes}
            value={selectedType}
            onChange={(event, newValue) => setSelectedType(newValue || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Type"
                variant="outlined"
                size="small"
              />
            )}
            sx={{ width: 300, marginBottom: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="contained"
              color="primary"
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
                height: '40px',
              }}
              onClick={handleSubmit}
              disabled={!selectedType}
            >
              Submit
            </Button>
          </Box>
        </Box>
      )}

      {/* Result Content */}
      {tabIndex === 1 && (
        <Box
          sx={{
            marginTop: 4,
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px solid #ccc',
            marginBottom: 2,
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Output
          </Typography>
          {apiResult?.report ? (
            <Typography
              variant="body1"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
            >
              {String(apiResult.report)}
            </Typography>
          ) : (
            <Typography variant="body2">No result available</Typography>
          )}
          {apiResult?.report && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPDF}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
                mt: 2,
              }}
              startIcon={<Download />}
            >
              Download PDF
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DQ;